import { useEffect, useMemo, useState } from "react";
import { useCustom, useCustomMutation, usePermissions, useTranslate } from "@refinedev/core";
import { ActionBar, Button, FormCard, SectionHeader } from "@fawkes/ui";
import { Plus, Trash2 } from "lucide-react";

import "../pensionplanning/AccommodatieBeheer.css";
import "./PensionCapaciteit.css";
import { IndeelbareTabel } from "../../components/IndeelbareTabel";

// settings-pension: "Capaciteit" — per diersoort hoeveel dieren er tegelijk in het pension kunnen. De wizard Nieuwe
// reservering meldt in de stap Periode de dagen die daar overheen gaan. Wie de hokken al in de kennelplanning heeft
// staan, vinkt "bereken uit de kennelplanning" aan en houdt niets dubbel bij. Wijzigen vraagt ROLE_PENSION_ADMIN,
// net als de api.

interface Soort {
  diersoortId: number;
  naam: string | null;
  /** Wat hier is ingevuld; leeg betekent: geen grens voor deze soort. */
  plaatsen: number | null;
  kennels: number;
  kennelPlaatsen: number;
  effectief: number | null;
}

interface Capaciteit {
  vestigingId: number;
  vestigingCode: string;
  uitKennelplanning: boolean;
  soorten: Soort[];
}

interface Diersoort {
  id: number;
  /** De feed animals/reference/species noemt het label; oudere feeds naam. */
  label?: string | null;
  naam?: string | null;
}

const soortNaam = (d: Diersoort) => d.label ?? d.naam ?? String(d.id);

/** Leeg is geen nul maar "geen grens", dus dat blijft null. */
function getal(waarde: string): number | null {
  const schoon = waarde.trim();
  if (schoon === "") return null;
  const n = Number(schoon);
  return Number.isFinite(n) && n >= 0 ? Math.trunc(n) : null;
}

export function PensionCapaciteit({ vestigingId, onBack }: { vestigingId: number; onBack: () => void }) {
  const t = useTranslate();
  const url = `pension/capaciteit?vestigingId=${vestigingId}`;
  const { data, isLoading, isError, refetch } = useCustom<Capaciteit>({ url, method: "get" });
  const soorten = useCustom<Diersoort[]>({ url: "animals/reference/species", method: "get" });
  const { mutate, isLoading: bezig } = useCustomMutation();
  const { data: rechten } = usePermissions<string[]>();
  const magBeheren = (rechten ?? []).includes("ROLE_PENSION_ADMIN");

  const [uitPlanning, setUitPlanning] = useState(false);
  const [waarden, setWaarden] = useState<Record<number, string>>({});
  /** Diersoorten die de gebruiker zelf toevoegde; die staan nog niet in het antwoord van de api. */
  const [extra, setExtra] = useState<number[]>([]);
  const [opgeslagen, setOpgeslagen] = useState(false);
  const [fout, setFout] = useState(false);

  const huidig = data?.data;
  useEffect(() => {
    if (!huidig) return;
    setUitPlanning(huidig.uitKennelplanning);
    setWaarden(
      Object.fromEntries((huidig.soorten ?? []).map((s) => [s.diersoortId, s.plaatsen == null ? "" : String(s.plaatsen)])),
    );
    setExtra([]);
  }, [huidig]);

  const alleSoorten = Array.isArray(soorten.data?.data) ? soorten.data.data : [];
  const naamVan = (id: number, terugval: string | null) => {
    const soort = alleSoorten.find((d) => d.id === id);
    return soort ? soortNaam(soort) : (terugval ?? String(id));
  };

  const rijen = useMemo(() => {
    const uitApi = huidig?.soorten ?? [];
    const leeg = extra
      .filter((id) => !uitApi.some((s) => s.diersoortId === id))
      .map<Soort>((id) => ({ diersoortId: id, naam: null, plaatsen: null, kennels: 0, kennelPlaatsen: 0, effectief: null }));
    return [...uitApi, ...leeg]
      .map((s) => ({ ...s, naam: naamVan(s.diersoortId, s.naam) }))
      .sort((a, b) => (a.naam ?? "").localeCompare(b.naam ?? ""));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [huidig, extra, alleSoorten]);

  const teKiezen = alleSoorten.filter((d) => !rijen.some((r) => r.diersoortId === d.id));

  const zet = (id: number, waarde: string) => {
    setOpgeslagen(false);
    setWaarden((x) => ({ ...x, [id]: waarde }));
  };

  const bewaar = () => {
    setFout(false);
    setOpgeslagen(false);
    mutate(
      {
        url,
        method: "put",
        values: {
          uitKennelplanning: uitPlanning,
          soorten: rijen.map((r) => ({ diersoortId: r.diersoortId, plaatsen: getal(waarden[r.diersoortId] ?? "") })),
        },
        successNotification: false,
        errorNotification: false,
      },
      {
        onSuccess: () => {
          setOpgeslagen(true);
          void refetch();
        },
        onError: () => setFout(true),
      },
    );
  };

  /** Waarmee de beschikbaarheidscontrole straks rekent, met wat er nu in het scherm staat. */
  const effectief = (r: Soort): number | null => (uitPlanning ? r.kennelPlaatsen : getal(waarden[r.diersoortId] ?? ""));

  return (
    <FormCard>
      <SectionHeader>{t("instellingen.pension.capaciteit.title")}</SectionHeader>
      <div className="acc-beheer">
        {isLoading ? (
          <p className="fawkes-muted">{t("common.loading")}</p>
        ) : isError ? (
          <p className="fawkes-error">{t("instellingen.pension.capaciteit.loadFailed")}</p>
        ) : (
          <div className="acc-kaart">
            <p className="acc-leeg">{t("instellingen.pension.capaciteit.uitleg")}</p>

            <label className="cap-vink">
              <input
                type="checkbox"
                checked={uitPlanning}
                disabled={!magBeheren}
                onChange={(e) => {
                  setUitPlanning(e.target.checked);
                  setOpgeslagen(false);
                }}
              />
              <span>
                <strong>{t("instellingen.pension.capaciteit.uitKennelplanning")}</strong>
                <span className="cap-uitleg">{t("instellingen.pension.capaciteit.uitKennelplanningUitleg")}</span>
              </span>
            </label>

            {rijen.length === 0 ? (
              <p className="acc-leeg">{t("instellingen.pension.capaciteit.geenSoorten")}</p>
            ) : (
              <IndeelbareTabel
                tabelKey="pension-capaciteit"
                columns={[
                  {
                    key: "soort", header: t("instellingen.pension.capaciteit.soort"),
                    cell: (r) => r.naam ?? String(r.diersoortId),
                  },
                  {
                    key: "plaatsen", header: t("instellingen.pension.capaciteit.plaatsen"),
                    cell: (r) => (
                      <input
                        type="number"
                        min={0}
                        step={1}
                        aria-label={t("instellingen.pension.capaciteit.plaatsenVan",
                          { args: [r.naam ?? String(r.diersoortId)] })}
                        value={waarden[r.diersoortId] ?? ""}
                        disabled={!magBeheren || uitPlanning}
                        onChange={(e) => zet(r.diersoortId, e.target.value)}
                      />
                    ),
                  },
                  {
                    key: "kennelplanning", header: t("instellingen.pension.capaciteit.kennelplanning"),
                    cell: (r) => (
                      <span className="cap-planning">
                        {r.kennels === 0
                          ? t("instellingen.pension.capaciteit.geenHokken")
                          : t("instellingen.pension.capaciteit.hokken", { args: [r.kennels, r.kennelPlaatsen] })}
                      </span>
                    ),
                  },
                  {
                    key: "effectief", header: t("instellingen.pension.capaciteit.effectief"),
                    cell: (r) => {
                      const grens = effectief(r);
                      return (
                        <span className="cap-effectief">
                          {grens == null ? (
                            <span className="cap-geenGrens">{t("instellingen.pension.capaciteit.geenGrens")}</span>
                          ) : (
                            t("instellingen.pension.capaciteit.plaatsenAantal", { args: [grens] })
                          )}
                        </span>
                      );
                    },
                  },
                  // De actiekolom bestond alleen voor wie mag beheren; dat blijft zo.
                  ...(magBeheren ? [{
                    key: "acties", header: "", vast: true,
                    cell: (r: typeof rijen[number]) => (r.kennels === 0 ? (
                      <button
                        type="button"
                        className="cap-mini"
                        aria-label={t("instellingen.pension.capaciteit.verwijderen",
                          { args: [r.naam ?? String(r.diersoortId)] })}
                        onClick={() => {
                          setExtra((x) => x.filter((id) => id !== r.diersoortId));
                          zet(r.diersoortId, "");
                        }}
                      >
                        <Trash2 size={15} />
                      </button>
                    ) : null),
                  }] : []),
                ]}
                rows={rijen}
                getRowKey={(r) => String(r.diersoortId)}
                empty=""
              />
            )}

            {magBeheren && teKiezen.length > 0 && (
              <label className="cap-toevoegen">
                <Plus size={15} />
                <span>{t("instellingen.pension.capaciteit.soortToevoegen")}</span>
                <select
                  value=""
                  aria-label={t("instellingen.pension.capaciteit.soortToevoegen")}
                  onChange={(e) => {
                    const id = Number(e.target.value);
                    if (!Number.isFinite(id) || id === 0) return;
                    setExtra((x) => [...x, id]);
                    setOpgeslagen(false);
                  }}
                >
                  <option value="">{t("instellingen.pension.capaciteit.kies")}</option>
                  {teKiezen.map((d) => (
                    <option key={d.id} value={d.id}>
                      {soortNaam(d)}
                    </option>
                  ))}
                </select>
              </label>
            )}

            {!magBeheren && <p className="acc-leeg">{t("instellingen.pension.capaciteit.alleenLezen")}</p>}
            {fout && (
              <p className="acc-fout" role="alert">
                {t("instellingen.pension.capaciteit.fout")}
              </p>
            )}
            {opgeslagen && <p className="acc-opgeslagen">{t("instellingen.pension.capaciteit.saved")}</p>}
          </div>
        )}
      </div>

      <ActionBar>
        {magBeheren && (
          <Button variant="primary" onClick={bewaar} disabled={bezig || isLoading || isError}>
            {t("common.save")}
          </Button>
        )}
        <Button variant="secondary" onClick={onBack} disabled={bezig}>
          {t("common.back")}
        </Button>
      </ActionBar>
    </FormCard>
  );
}
